/*
 * omes-brand-follow.js
 * ---------------------------------------------------------------------------
 * Real "Follow the brand" behaviour, backed by the omes-smile loyalty app
 * (omes_loyality_v2) via its Shopify App Proxy.
 *
 * Storefront <-> app-proxy contract (keep in sync with the loyalty app route
 * app/routes/apps.loyalty.brands.tsx):
 *
 *   GET  /apps/loyalty/brands
 *     -> { authenticated, followed: string[], max, remaining, reward? }
 *   POST /apps/loyalty/brands  { brandHandle, action: "follow"|"unfollow" }
 *     -> { success, following, pointsAwarded?, reason? }
 *
 * Wires every `[data-follow][data-brand]` element on the page (buttons or
 * anchors), including ones inserted later (the click handler is delegated).
 * `data-brand` is the brand's collection handle — sections get it from
 * snippets/omes-brand-handle.liquid. Resilient: if the endpoint is
 * unreachable or the customer is logged out, buttons stay in their idle state
 * and a logged-out customer who clicks is sent to login and back.
 *
 * After every saved follow/unfollow it fires `omes:brand-follow-change`
 * { brand, following, pointsAwarded } — Favoris › Marques suivies
 * (assets/omes-followed-brands.js) listens to it.
 *
 * Since 21 Oct 2026 the OMES account (sections/mon-compte.liquid) comes first:
 * with its token in the browser, the follows are the marketplace account's
 * (/account/brands), same answers, same events; the proxy stays for a customer
 * still signed in with Shopify. See « The OMES account's follows » below.
 */
(function () {
  "use strict";

  if (window.__omesBrandFollowLoaded) return;
  window.__omesBrandFollowLoaded = true;

  var ENDPOINT = "/apps/loyalty/brands";
  // Favoris page (settings.wishlist_page), « Marques suivies » tab.
  var FOLLOWED_URL = "/pages/favoris?vue=marques";
  // Cap reported by the last GET; used in the « limit reached » message.
  var knownMax = 0;

  // ── The OMES account's follows ─────────────────────────────────────────────
  // Since 21 Oct 2026 the customer's account on omes.tn is the marketplace's own
  // (sections/mon-compte.liquid; its token sits in localStorage `omes_customer`,
  // the contract with assets/mon-compte.js). When that token is there, the follows
  // are the account's — the marketplace's /account/brands — answered in the same
  // shape as the loyalty proxy; without it, the proxy (a Shopify-signed customer)
  // still answers, and a signed-out click goes to /pages/mon-compte.
  // Mirrored in assets/omes-followed-brands.js and assets/favoris.js.
  var ACCOUNT_API = (window.omesApiBase || "https://partenaires.omes.tn") + "/api/storefront/v1";
  var ACCOUNT_LOGIN = "/pages/mon-compte";
  function accountToken() {
    try {
      var s = JSON.parse(localStorage.getItem("omes_customer") || "null");
      return s && s.token && (!s.expiresAt || Date.parse(s.expiresAt) > Date.now()) ? s.token : null;
    } catch (e) { return null; }
  }
  function accountHeaders(token, json) {
    var h = { Accept: "application/json", "X-Customer-Token": token };
    if (json) h["Content-Type"] = "application/json";
    return h;
  }
  // { authenticated, followed (oldest first, like the proxy), max, remaining, reward }
  function accountFollows(token) {
    return fetch(ACCOUNT_API + "/account/brands", { headers: accountHeaders(token), mode: "cors", credentials: "omit" })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (d) {
        if (!d || !Array.isArray(d.brands)) return { authenticated: false, followed: [] };
        var followed = d.brands.map(function (b) { return b.handle; }).reverse();
        return { authenticated: true, followed: followed, max: Number(d.max) || 0, remaining: Math.max(0, (Number(d.max) || 0) - followed.length), reward: Number(d.reward) || 0 };
      });
  }
  // { status, ok, data: { success, following, pointsAwarded, reason } }
  function accountFollow(token, brand, action) {
    var req = action === "follow"
      ? fetch(ACCOUNT_API + "/account/brands", { method: "POST", headers: accountHeaders(token, true), mode: "cors", credentials: "omit", body: JSON.stringify({ handle: brand }) })
      : fetch(ACCOUNT_API + "/account/brands/" + encodeURIComponent(brand), { method: "DELETE", headers: accountHeaders(token), mode: "cors", credentials: "omit" });
    return req.then(function (r) {
      if (r.status === 401) return { status: 401, ok: false, data: null };
      if (r.status === 422) return { status: 422, ok: true, data: { success: false, reason: "limit_reached" } };
      if (!r.ok) return { status: r.status, ok: false, data: null };
      return r.json().catch(function () { return {}; }).then(function (d) {
        return { status: r.status, ok: true, data: { success: true, following: action === "follow", pointsAwarded: Number(d.rewarded) || 0 } };
      });
    });
  }

  function followButtons() {
    var nodes = document.querySelectorAll("[data-follow][data-brand]");
    var out = [];
    for (var i = 0; i < nodes.length; i++) {
      var brand = (nodes[i].getAttribute("data-brand") || "").trim();
      if (brand) out.push(nodes[i]);
    }
    return out;
  }

  // Resolve the element whose text we may swap between idle/following labels.
  // Never returns a container that holds child elements (e.g. a button with an
  // SVG icon), so we don't wipe icons; returns the element itself only when it
  // is text-only (e.g. brand-story anchors).
  function labelTarget(el) {
    var byData = el.querySelector("[data-follow-label]");
    if (byData) return byData;
    var byClass = el.querySelector(".ombc__follow-label");
    if (byClass) return byClass;
    if (el.children.length === 0) return el;
    return null;
  }

  function idleLabel(el) {
    if (el.dataset.followIdle != null) return el.dataset.followIdle;
    var attr = el.getAttribute("data-follow-text");
    if (attr != null) return attr;
    var target = labelTarget(el);
    return target ? target.textContent.trim() : "";
  }

  function followingLabel(el) {
    return el.getAttribute("data-following") || "Suivi";
  }

  // Apply follow state to every button that points at the same brand.
  function applyState(brand, on) {
    var buttons = followButtons();
    for (var i = 0; i < buttons.length; i++) {
      var el = buttons[i];
      if ((el.getAttribute("data-brand") || "").trim() !== brand) continue;
      el.classList.toggle("is-following", on);
      el.setAttribute("aria-pressed", on ? "true" : "false");
      var target = labelTarget(el);
      if (target) target.textContent = on ? followingLabel(el) : idleLabel(el);
    }
  }

  function isFollowing(el) {
    return el.classList.contains("is-following");
  }

  // A signed-out shopper signs in on the OMES account page and comes back here.
  function redirectToLogin() {
    // A token the marketplace refused is stale: drop it so the account page shows the login.
    if (accountToken()) { try { localStorage.removeItem("omes_customer"); } catch (e) {} }
    var here = window.location.pathname + window.location.search;
    window.location.href = ACCOUNT_LOGIN + "?retour=" + encodeURIComponent(here);
  }

  // ── Notice: one small message at the bottom of the screen ────────────────
  var noticeEl = null;
  var noticeTimer = null;

  function injectNoticeStyle() {
    if (document.getElementById("omes-follow-notice-style")) return;
    var css =
      ".omes-follow-notice{position:fixed;left:50%;bottom:calc(76px + env(safe-area-inset-bottom,0px));" +
      "transform:translate(-50%,12px);z-index:2147483000;box-sizing:border-box;width:max-content;" +
      "max-width:calc(100vw - 32px);padding:12px 16px;border-radius:4px;background:#111;color:#fff;" +
      "font-size:14px;line-height:1.4;box-shadow:0 6px 24px rgba(0,0,0,.18);opacity:0;" +
      "pointer-events:none;transition:opacity .2s,transform .2s}" +
      ".omes-follow-notice.is-on{opacity:1;transform:translate(-50%,0);pointer-events:auto}" +
      ".omes-follow-notice a{color:#fff;text-decoration:underline;margin-left:8px;white-space:nowrap;" +
      "display:inline-block;padding:4px 0}" +
      "@media (min-width:1024px){.omes-follow-notice{bottom:24px}}";
    var style = document.createElement("style");
    style.id = "omes-follow-notice-style";
    style.textContent = css;
    document.head.appendChild(style);
  }

  function notice(text, linkText) {
    if (!noticeEl) {
      injectNoticeStyle();
      noticeEl = document.createElement("div");
      noticeEl.className = "omes-follow-notice";
      noticeEl.setAttribute("role", "status");
      noticeEl.setAttribute("aria-live", "polite");
      document.body.appendChild(noticeEl);
    }
    noticeEl.textContent = text;
    // No link to the list when we are already on it.
    if (linkText && window.location.pathname.indexOf("/pages/favoris") !== 0) {
      var a = document.createElement("a");
      a.href = FOLLOWED_URL;
      a.textContent = linkText;
      noticeEl.appendChild(a);
    }
    noticeEl.classList.add("is-on");
    clearTimeout(noticeTimer);
    noticeTimer = setTimeout(function () {
      noticeEl.classList.remove("is-on");
    }, 5000);
  }

  function announce(brand, following, points) {
    document.dispatchEvent(
      new CustomEvent("omes:brand-follow-change", {
        detail: { brand: brand, following: following, pointsAwarded: points },
      })
    );
    if (!following) { notice("Marque retirée de vos favoris"); return; }
    notice(
      points > 0 ? "Marque suivie · +" + points + " points O'Club" : "Marque suivie",
      "Mes marques"
    );
  }

  // { status, ok, data } from the account when signed in there, else from the proxy.
  function request(brand, action) {
    var token = accountToken();
    if (token) return accountFollow(token, brand, action);
    return fetch(ENDPOINT, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ brandHandle: brand, action: action }),
    }).then(function (r) {
      if (r.status === 401) return { status: 401, ok: false, data: null };
      if (!r.ok) return { status: r.status, ok: false, data: null };
      return r.json().then(function (d) { return { status: r.status, ok: true, data: d }; });
    });
  }

  function handleClick(e) {
    var el = e.target.closest("[data-follow][data-brand]");
    if (!el) return;
    e.preventDefault();

    var brand = (el.getAttribute("data-brand") || "").trim();
    if (!brand || el.getAttribute("aria-busy") === "true") return;

    var wantFollow = !isFollowing(el);
    el.setAttribute("aria-busy", "true");

    request(brand, wantFollow ? "follow" : "unfollow")
      .then(function (r) {
        if (r.status === 401) {
          redirectToLogin();
          return null;
        }
        if (!r.ok) throw new Error("HTTP " + r.status);
        return r.data;
      })
      .then(function (data) {
        if (!data) return;
        if (data.success) {
          applyState(brand, !!data.following);
          announce(brand, !!data.following, Number(data.pointsAwarded) || 0);
        } else if (data.reason === "limit_reached") {
          // State stays unfollowed; say why and where to make room.
          document.dispatchEvent(
            new CustomEvent("omes:brand-follow-limit", { detail: { brand: brand } })
          );
          notice(
            knownMax > 0
              ? "Vous suivez déjà " + knownMax + " marques. Retirez-en une pour suivre celle-ci."
              : "Vous suivez déjà le maximum de marques. Retirez-en une pour suivre celle-ci.",
            "Gérer mes marques"
          );
        }
      })
      .catch(function (err) {
        console.error("[omes-brand-follow] request failed", err);
        notice("Impossible d'enregistrer pour le moment. Réessayez dans un instant.");
      })
      .then(function () {
        el.removeAttribute("aria-busy");
      });
  }

  // On load, seed idle labels and reflect the customer's current follows.
  function init() {
    var buttons = followButtons();
    for (var i = 0; i < buttons.length; i++) {
      var el = buttons[i];
      if (el.dataset.followIdle == null) el.dataset.followIdle = idleLabel(el);
      if (!el.hasAttribute("aria-pressed")) el.setAttribute("aria-pressed", "false");
    }
    if (!buttons.length) return;

    var token = accountToken();
    (token
      ? accountFollows(token)
      : fetch(ENDPOINT, { credentials: "include", headers: { Accept: "application/json" } }).then(function (r) { return r.ok ? r.json() : null; }))
      .then(function (data) {
        if (!data || !data.authenticated || !Array.isArray(data.followed)) return;
        knownMax = Number(data.max) || 0;
        var followed = {};
        for (var j = 0; j < data.followed.length; j++) followed[data.followed[j]] = true;
        var els = followButtons();
        for (var k = 0; k < els.length; k++) {
          var brand = (els[k].getAttribute("data-brand") || "").trim();
          if (followed[brand]) applyState(brand, true);
        }
      })
      .catch(function () {
        /* logged out or endpoint unavailable: leave buttons idle */
      });
  }

  document.addEventListener("click", handleClick);

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
